import React, { useState } from 'react';
import { DatePicker, Button } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import Privileges from 'lib/constants/privileges';

// "Contract Invoice Comparison" report launcher. Pick a month; the report compares that
// month's contract invoices against the previous month (by contract) and lists new,
// missing, and changed contracts plus contracts expiring that month. The PDF itself is
// generated server-side by the file route GET /v1/files/contract_invoice_comparison
// (resources/files/contract_invoice_comparison.js) -- exactly like the Sales Tax /
// Received Payments downloads -- so this screen only picks the month and opens the URL.
//
// Reached from a toolbar button on the Contracts search screen (resources/contracts.js).
// Superuser-only for now: the file's privilege file_read_contract_invoice_comparison is
// auto-seeded to 0 roles; superusers bypass.

const { MonthPicker } = DatePicker;
const ROOT_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function ContractInvoiceComparison({ history }) {
    // Default to NEXT month: the report is run during the current month to prep the next
    // month's batch, so e.g. running in August pre-selects September (with August as the
    // comparison/previous month). The user can still override to any month before download.
    const [month, setMonth] = useState(moment().add(1, 'month').startOf('month'));

    const download = () => {
        if (!month) return;
        // Cache-buster: window.open re-serves an identical URL from browser cache, which would
        // show a stale report after new invoices are generated. The backend ignores _t.
        const url = `${ROOT_URL}/v1/files/contract_invoice_comparison`
            + `?month=${month.format('YYYY-MM')}&_t=${Date.now()}`;
        // Open the report in a new tab (the file route serves Content-Disposition: inline, so the
        // browser's PDF viewer renders it there), then send this tab back to the Contracts list so
        // the user lands on the contracts UI rather than being stranded on the launcher screen.
        window.open(url, '_blank');
        history.push('/contracts');
    };

    return <div style={{margin: 'auto', maxWidth: '760px', padding: '24px'}}>
        <h1 style={{fontFamily: 'Raleway'}}>Contract Invoice Comparison</h1>
        <p style={{color: '#666'}}>
            Compare a month's contract invoices against the previous month. The PDF shows each
            month's invoice count and total, then — by contract — the new invoices, the ones
            missing versus last month, and the ones whose billed amount changed, plus the
            contracts expiring within the selected month. Voided invoices are excluded.
        </p>

        <div style={{display: 'flex', alignItems: 'flex-end', gap: '12px', marginTop: '20px'}}>
            <div>
                <div style={{fontSize: '12px', color: '#888', marginBottom: '4px'}}>Month</div>
                <MonthPicker
                    value={month}
                    onChange={setMonth}
                    allowClear={false}
                    format="MMMM YYYY"
                    style={{width: '200px'}} />
            </div>
            <Button type="primary" icon="file-pdf" disabled={!month} onClick={download}>
                Download Report PDF
            </Button>
        </div>
    </div>;
}

ContractInvoiceComparison.requiredPrivileges = [Privileges.FILE_READ_CONTRACT_INVOICE_COMPARISON];

export default withRouter(ContractInvoiceComparison);
